import { useQuery } from '@tanstack/react-query';
import { PerformanceDataSet } from 'src/@types/performance';
import {
	ONE_DAY_MS,
	Serializer,
	UISerializableInsuranceFundRecord,
} from '@velocity-exchange/common';
import { getSpotMarketSymbol } from 'src/utils/DataApiClient';
import StatsApiClient from 'src/utils/StatsApiClient';
import { applyTimeFilter } from 'src/utils/pnlChart';
import { JsonObject } from 'cerializr';

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

export function useInsuranceFundRecords(marketIndex: number) {
	return useQuery<PerformanceDataSet>({
		queryKey: ['insuranceFundRecords', marketIndex],
		staleTime: ONE_DAY_MS,
		queryFn: async () => {
			const symbol = getSpotMarketSymbol(marketIndex);
			if (!symbol) {
				return { '24h': [], '7d': [], '30d': [], all: [] };
			}

			const allRecords: Array<Record<string, unknown>> = [];
			let pageToken: string | undefined;

			do {
				const result = await StatsApiClient.fetchMarketInsuranceFundRecords(
					symbol,
					pageToken
				);

				if (!result.success || !result.records) {
					console.error('Failed to fetch IF records', result);
					break;
				}

				allRecords.push(...result.records);

				const getTs = (r: Record<string, unknown>): number | undefined => {
					const ts = r.ts;
					if (typeof ts === 'number') return ts;
					if (typeof ts === 'string') return parseInt(ts, 10);
					return undefined;
				};
				const oldestRecordTs = result.records
					.map((r) => getTs(r as Record<string, unknown>))
					.filter((ts): ts is number => ts !== undefined)
					.reduce((min, ts) => Math.min(min, ts), Infinity);
				const hasEnoughHistory =
					oldestRecordTs !== Infinity &&
					Date.now() - oldestRecordTs * 1000 >= NINETY_DAYS_MS;

				pageToken = result.meta?.nextPage ?? undefined;
				if (hasEnoughHistory || !pageToken) break;
			} while (pageToken);

			const allDataPoints = allRecords
				.map((record) =>
					Serializer.Deserialize.UIInsuranceFundRecord(record as JsonObject)
				)
				.filter(
					(record): record is UISerializableInsuranceFundRecord =>
						record !== null && !record.totalIfSharesAfter.eqZero()
				)
				.map((record) => {
					return {
						date: new Date(record.ts.toNumber() * 1000),
						totalPnl: record.insuranceVaultAmountBefore
							.add(record.amount)
							.scale(1000000, record.totalIfSharesAfter.toNum() * 1000000)
							.toNum(),
					};
				})
				.sort((a, b) => a.date.getTime() - b.date.getTime());

			return {
				'24h': applyTimeFilter(allDataPoints, '24h', false),
				'7d': applyTimeFilter(allDataPoints, '7d', false),
				'30d': applyTimeFilter(allDataPoints, '30d', false),
				all: applyTimeFilter(allDataPoints, 'all', false),
			};
		},
	});
}
